// ── Bank Import — upload a borrower's bank export ──
//
// For borrowers whose banks Plaid cannot link. The operator drops the file the
// bank gave them, the page says what it found and whether it trusts it, and
// only then offers to post it.
//
// THE CHECKS ARE THE PAGE. Everything else here is a file input and a table;
// what this screen exists to do is stand between a bank file and the portfolio
// tables and refuse the ones that are wrong. So the checks are not a collapsed
// "details" section under a success message — they are the first thing below
// the fold, each one saying what it proved, and a failed one takes the Post
// button away rather than colouring it red and letting it be clicked anyway.
//
// NOTHING IS UPLOADED UNTIL A PREVIEW HAS RUN AND BEEN LOOKED AT. The commit
// call sends back the previewKey the preview returned; the server recomputes it
// from the bytes and the chosen account and refuses if either moved.
import { useState, useEffect, useRef } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, Btn, Card, Th, Td, TdNum, STh,
         TabBtn, Field, FSelect } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { hasFundedTerm } from "../../../shared/accountStatus";
import { useApp } from "../../../shared/appContext";

const MAX_MB = 20;

// The status vocabulary is deliberately small and blunt. A reader should not
// have to learn what four shades of amber mean.
const TONE = {
  pass:            { fg: "#1a7f4b", bg: "#e8f5ee", label: "OK" },
  ok:              { fg: "#1a7f4b", bg: "#e8f5ee", label: "OK" },
  clean:           { fg: "#1a7f4b", bg: "#e8f5ee", label: "OK" },
  review:          { fg: "#8a6100", bg: "#fdf3e0", label: "REVIEW" },
  "not available": { fg: "var(--color-text-muted)", bg: "var(--color-background-secondary)", label: "N/A" },
  // Distinct from OK on purpose: the importer changed what the file said, and
  // that is the one thing on this screen the reader must not skim past.
  folded:          { fg: "#0b6f8a", bg: "#e4f4f9", label: "FOLDED" },
  empty:           { fg: "#8a6100", bg: "#fdf3e0", label: "EMPTY" },
  FAIL:            { fg: "#b3261e", bg: "#fdecea", label: "FAIL" },
  BLOCKING:        { fg: "#b3261e", bg: "#fdecea", label: "STOP" },
};

const Pill = ({ status }) => {
  const t = TONE[status] || TONE.review;
  return <span style={{ color:t.fg, background:t.bg, padding:"2px 8px", borderRadius:4,
    fontSize:11, fontWeight:600, letterSpacing:0.3, whiteSpace:"nowrap" }}>{t.label}</span>;
};

// The synthetic account key a manual feed is filed under. It mirrors the shape
// the Plaid rows use (see ace_bank_import.sql) so that every existing query,
// view and report treats a manual account exactly like a linked one — the
// 'man:' prefix is the only thing that distinguishes it, and sourcetype is what
// a reader checks.
//
// IT MUST BE STABLE. It is the account half of every transaction's identity, so
// a key that came out differently on a later upload would file the same
// borrower's history twice under two accounts.
export const manualAccountKey = (accountid, bankAccount) =>
  `man:${accountid}:${bankAccount.accountnumberlast4 || bankAccount.id}`;

const bankAccountLabel = (b) => {
  const bits = [b.bankname || "Account"];
  if (b.accountnumberlast4) bits.push("\u2022\u2022" + b.accountnumberlast4);
  if (b.description) bits.push("(" + b.description + ")");
  // A closed account is still shown — its history is real and may well be what
  // is being imported — but saying so stops it being picked by accident.
  if (b.accountcloseddate) bits.push("[closed " + b.accountcloseddate + "]");
  return bits.join(" ");
};

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(new Error("The file could not be read."));
  // readAsDataURL gives 'data:<type>;base64,<payload>'. The server strips the
  // prefix; sending the whole thing keeps the type visible in the request,
  // which is worth having when a bank changes what it serves.
  r.onload = () => resolve(String(r.result));
  r.readAsDataURL(file);
});

export const BankImportPage = () => {
  const { api, role } = useApp();
  const [tab, setTab] = useState("upload");
  if (!role.isAdmin) return <ErrorMsg msg="Only an administrator can import bank files." />;

  return <>
    <SectionHeader actions={[
      <TabBtn key="u" id="upload"    current={tab} label="Upload"    onClick={setTab} />,
      <TabBtn key="h" id="history"   current={tab} label="History"   onClick={setTab} />,
      <TabBtn key="t" id="templates" current={tab} label="Templates" onClick={setTab} />,
    ]} />
    {tab === "upload"    && <UploadTab api={api} />}
    {tab === "history"   && <HistoryTab api={api} />}
    {tab === "templates" && <TemplatesTab api={api} />}
  </>;
};

// ── Upload ──────────────────────────────────────────────────────────────────
const UploadTab = ({ api }) => {
  const [file, setFile] = useState(null);
  const [b64, setB64] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountid, setAccountid] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [plaidaccountid, setPlaidaccountid] = useState("");
  // Whether THIS file's format names an account of its own. Held apart from
  // `result` because choosing an account clears the preview, and the fact that
  // an account was needed has to outlive the preview that said so — otherwise
  // the picker vanishes the instant it is used.
  const [needsAccount, setNeedsAccount] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [posted, setPosted] = useState(null);
  // Balances typed by hand, per account key, for a file that carries none.
  // { [key]: { asof, current, available } } — a blank Current skips the account.
  const [manualBal, setManualBal] = useState({});
  const inputRef = useRef(null);

  // ONLY BORROWERS WITH A FUNDED TERM. An account that has not funded has no
  // loan to hang bank activity off, so offering it here is offering a way to
  // file a borrower's statements against a deal that never closed — and
  // accountstatus already answers the question, so this is not a second
  // definition of "funded". See shared/accountStatus.js.
  useEffect(() => {
    api.get("getAccounts").then((d) => setAccounts((Array.isArray(d) ? d : []).filter(hasFundedTerm)))
      .catch(() => setAccounts([]));
  }, []);

  // FROM dbo.BankAccounts, NOT FROM THE PLAID CONNECTIONS.
  //
  // The obvious source is plaidConnections, and it is the wrong one: it lists
  // the accounts already linked through Plaid, and a borrower whose bank Plaid
  // cannot link has none. That is the entire population this page exists for,
  // so sourcing it there makes the list reliably empty for every borrower who
  // needs it.
  //
  // BankAccounts is the right table — it is where the read-only online banking
  // credentials live, so a borrower whose statements get downloaded by hand
  // already has a row there. getBankAccounts returns the last four only, never
  // the full number, which is also all anyone needs to recognise an account.
  useEffect(() => {
    setPlaidaccountid("");
    if (!accountid) { setBankAccounts([]); return; }
    api.get("getBankAccounts", { accountid })
      .then((d) => setBankAccounts(Array.isArray(d) ? d : []))
      .catch(() => setBankAccounts([]));
  }, [accountid]);

  // ANY CHANGE INVALIDATES THE PREVIEW. The preview is a statement about a
  // specific file posted to a specific borrower; leaving it on screen after
  // either has changed would be inviting a commit against something the reader
  // never looked at. The server refuses that too — this just stops it looking
  // like it would have worked.
  useEffect(() => { setResult(null); setPosted(null); }, [accountid, plaidaccountid, b64]);
  useEffect(() => { setNeedsAccount(false); }, [b64]);

  const pick = async (f) => {
    setErr(null); setResult(null); setPosted(null);
    if (!f) { setFile(null); setB64(null); return; }
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`${f.name} is ${(f.size / 1048576).toFixed(1)}MB. The limit is ${MAX_MB}MB — `
           + `export a narrower date range rather than a whole year.`);
      setFile(null); setB64(null); return;
    }
    setFile(f);
    try { setB64(await readAsBase64(f)); }
    catch (e) { setErr(e.message); setFile(null); setB64(null); }
  };

  const run = async (action) => {
    setBusy(true); setErr(null);
    try {
      const body = { action, filedata: b64, filename: file.name,
                     accountid: accountid || null, plaidaccountid: plaidaccountid || null };
      if (action === "commit") {
        body.previewKey = result.previewKey;
        const mb = Object.entries(manualBal)
          .filter(([, v]) => v && String(v.current || "").trim() !== "")
          .map(([key, v]) => ({ key, asofDate: v.asof, current: money(v.current),
                                available: String(v.available || "").trim() === "" ? null : money(v.available) }));
        if (mb.some((b) => !Number.isFinite(b.current) || (b.available !== null && !Number.isFinite(b.available)))) {
          setErr("A balance is not a number. Use digits, with an optional $ and commas.");
          setBusy(false); return;
        }
        if (mb.length) body.manualBalances = mb;
      }
      const r = await api.post("uploadBankFile", body);
      if (action === "commit") { setPosted(r); setResult({ ...result, committed: true }); }
      else { setResult(r); setNeedsAccount(r.sections.some((s) => s.needsAccount)); setManualBal({}); }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const canPreview = !!b64 && !!accountid && !busy;
  const canCommit  = result && !result.blocked && !result.committed && !!accountid && !busy;

  return <>
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))", gap:12 }}>
        <Field label="Borrower" required>
          <FSelect value={accountid} onChange={setAccountid}
            options={[{ value:"", label:"Select a borrower..." }].concat(
              accounts.map((a) => ({ value:a.id, label:a.companyname })))} />
        </Field>
        {/* ASKED ONLY WHEN THE FILE CANNOT ANSWER IT. Three of the four formats
            name the account themselves — two in a column, one in the filename —
            and for those this field is noise that implies a decision the
            operator does not get to make. It appears when a preview reports the
            format names none. */}
        {needsAccount && <Field label="Which account is this?" required>
          <FSelect value={plaidaccountid} onChange={setPlaidaccountid} disabled={!accountid}
            options={[{ value:"", label:"Select the account..." }].concat(
              bankAccounts.map((b) => ({ value: manualAccountKey(accountid, b),
                label: bankAccountLabel(b) })))} />
        </Field>}
        <Field label="File">
          <input ref={inputRef} type="file" accept=".csv,.CSV,.xls,.xlsx"
            onChange={(e) => pick(e.target.files && e.target.files[0])}
            style={{ fontSize:13, padding:"6px 0" }} />
        </Field>
      </div>

      {needsAccount && bankAccounts.length === 0 && <div style={{ marginTop:10, fontSize:12, color:"#8a6100" }}>
        This borrower has no bank accounts on file, and this file does not name one — so there is
        nothing to file these rows against. Add the account under Bank Accounts on the borrower's
        record first; that row is also where the online banking credentials live.
      </div>}
      {needsAccount && bankAccounts.length > 0 && <div style={{ marginTop:10, fontSize:12, color:"var(--color-text-muted)" }}>
        This export does not name the account anywhere in it, so whatever is chosen above is what the
        rows are filed against. Nothing downstream can catch a wrong choice here.
      </div>}

      <div style={{ marginTop:12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        <Btn onClick={() => run("preview")} variant="primary" disabled={!canPreview}>
          {busy && !result ? "Reading..." : "Preview"}
        </Btn>
        {result && <Btn onClick={() => run("commit")} variant="primary" disabled={!canCommit}>
          {busy ? "Posting..." : "Post to the portfolio"}
        </Btn>}
        {file && <span style={{ fontSize:12, color:"var(--color-text-muted)" }}>
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </span>}
        {!accountid && b64 && <span style={{ fontSize:12, color:"#8a6100" }}>
          Choose a borrower first.
        </span>}
      </div>
    </Card>

    {err && <ErrorMsg msg={err} />}

    {posted && <Card style={{ marginBottom:12, borderLeft:`3px solid #1a7f4b` }}>
      <div style={{ fontWeight:600, marginBottom:4 }}>Posted</div>
      <div style={{ fontSize:13 }}>
        {posted.rows.new} new {posted.rows.new === 1 ? "row" : "rows"}
        {posted.rows.updated > 0 && <> · {posted.rows.updated} already present, updated in place</>}
        {posted.rows.balances > 0 && <> · {posted.rows.balances} balance{posted.rows.balances === 1 ? "" : "s"} recorded</>}
        {" "}· run #{posted.runId}
      </div>
    </Card>}

    {result && result.seenBefore && result.seenBefore.length > 0 &&
      <Card style={{ marginBottom:12, borderLeft:`3px solid ${B.amber}` }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>This exact file has been posted before</div>
        {/* Not an error — the identity keys make a re-post a no-op. But three of
            four sample exports from one borrower turned out to be the same
            download saved more than once, so it is worth saying out loud. */}
        <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          {result.seenBefore.map((p) => `run #${p.id} on ${fmtDate(p.uploadedutc)} (${p.rowsnew} new)`).join(" · ")}
          {" "}— posting it again will not duplicate anything, but it may mean the wrong window was downloaded.
        </div>
      </Card>}

    {result && result.blocked &&
      <Card style={{ marginBottom:12, borderLeft:"3px solid #b3261e" }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>This file will not be posted</div>
        <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          {result.sections.some((s) => s.needsAccount)
            ? "This export does not name an account. Choose which one it is above, then preview again."
            : result.blockedReason}
        </div>
      </Card>}

    {result && result.unmatched && result.unmatched.length > 0 &&
      <AccountMapper api={api} accountid={accountid} unmatched={result.unmatched}
        bankAccounts={result.bankAccounts || []} onSaved={() => run("preview")} />}

    {result && !result.balancesInFile && !result.blocked && !result.committed &&
      <ManualBalances result={result} value={manualBal} onChange={setManualBal} />}

    {result && result.sections.map((s, i) => <SectionCard key={i} s={s} />)}
  </>;
};

// ── tying the file's accounts to the borrower's ────────────────────────────
//
// Answered here rather than in a settings screen because this is the moment the
// question has an obvious answer: the account number is on screen, next to the
// rows that came with it. Asked once per spelling and then remembered, so a
// monthly upload stops asking after the first one.
const AccountMapper = ({ api, accountid, unmatched, bankAccounts, onSaved }) => {
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // Sequentially, not in parallel: they all write to one table keyed on the
      // same borrower, and a failure halfway through should leave the earlier
      // answers saved rather than in an unknown state.
      for (const u of unmatched) {
        const id = choice[u.key];
        if (!id) continue;
        await api.post("saveBankImportAccountMap",
          { accountid, fileaccountkey: u.key, bankaccountid: Number(id) });
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const anyChosen = unmatched.some((u) => choice[u.key]);

  return <Card style={{ marginBottom:12, borderLeft:`3px solid ${B.amber}` }}>
    <div style={{ fontWeight:600, marginBottom:4 }}>
      Which of this borrower&apos;s bank accounts are these?
    </div>
    <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginBottom:10 }}>
      The file names these accounts, but nothing on this borrower&apos;s record matches them. Tie each
      one to a bank account and it will be remembered — this is asked once per account, not once per
      upload.
    </div>

    {bankAccounts.length === 0
      ? <div style={{ fontSize:13, color:"#8a6100" }}>
          This borrower has no bank accounts on file at all. Add them under Bank Accounts on the
          borrower&apos;s record first — that row is also where the online banking credentials live.
        </div>
      : <>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr><Th>In the file</Th><Th>Why it did not match</Th><Th>Is this...</Th></tr></thead>
          <tbody>{unmatched.map((u) => (
            <tr key={u.key}>
              <Td style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{u.key}</Td>
              <Td style={{ color:"var(--color-text-muted)", fontSize:12 }}>{u.reason}</Td>
              <Td style={{ minWidth:260 }}>
                <FSelect value={choice[u.key] || ""}
                  onChange={(v) => setChoice({ ...choice, [u.key]: v })}
                  options={[{ value:"", label:"Select an account..." }].concat(
                    bankAccounts.map((b) => ({ value:String(b.id), label:b.label })))} />
              </Td>
            </tr>
          ))}</tbody>
        </table>
        <div style={{ marginTop:10, display:"flex", gap:8, alignItems:"center" }}>
          <Btn onClick={save} variant="primary" disabled={!anyChosen || busy}>
            {busy ? "Saving..." : "Save and preview again"}
          </Btn>
          {!anyChosen && <span style={{ fontSize:12, color:"var(--color-text-muted)" }}>
            Choose at least one.
          </span>}
        </div>
      </>}
    {err && <div style={{ marginTop:8, fontSize:12, color:"#b3261e" }}>{err}</div>}
  </Card>;
};

// "2026-06-08" → "06/08/2026". Anything that is not an ISO date passes through.
const mdy = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (iso || "—");
};

// "$1,234.56" / "(1,234.56)" / "-1234.56" → number. NaN when it is not one.
const money = (v) => {
  let t = String(v == null ? "" : v).trim().replace(/[$,\s]/g, "");
  if (/^\(.*\)$/.test(t)) t = "-" + t.slice(1, -1);
  return t === "" ? NaN : Number(t);
};

// ── balances by hand ────────────────────────────────────────────────────────
// Shown when the file has transactions but no balances (Iceberg's Old National
// CSV). One line per account in the file: the balance as of a date — the
// account's last transaction in the file unless changed. Leave Current blank to skip an account. Posted
// with the transactions, in the same run.
const ManualBalances = ({ result, value, onChange }) => {
  const today = new Date().toISOString().slice(0, 10);
  const accts = [];
  const seen = new Set();
  result.sections.forEach((s) => s.accounts.forEach((a) => {
    if (!seen.has(a.key)) { seen.add(a.key); accts.push(a); }
  }));
  if (!accts.length) return null;
  // Default date: the account's last transaction in this file — the balance a
  // statement download ends on. Today only if the file gives no usable date.
  const lastTxn = {};
  accts.forEach((a) => {
    const d = String(a.to || "").slice(0, 10);
    lastTxn[a.key] = /^\d{4}-\d{2}-\d{2}$/.test(d) && d <= today ? d : today;
  });
  const get = (k) => value[k] || { asof: lastTxn[k] || today, current: "", available: "" };
  const set = (k, f, v) => onChange({ ...value, [k]: { ...get(k), [f]: v } });
  const inp = { fontSize: 13, padding: "5px 8px", border: "1px solid #c8cdd2", borderRadius: 4 };
  return (
    <Card style={{ marginBottom: 12, borderLeft: `3px solid ${B.cyan}` }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>This file has no balances</div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
        Enter each account&apos;s balance as of a date and it is recorded with this upload. The date
        defaults to the account&apos;s last transaction in this file; change it if your balance is from
        another day. Leave Current blank to skip an account.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><Th>Account</Th><Th>Tied to</Th><Th>Last transaction</Th><Th>Balance as of</Th><Th>Current</Th><Th>Available (optional)</Th></tr></thead>
        <tbody>{accts.map((a) => {
          const v = get(a.key);
          const bad = String(v.current || "").trim() !== "" && !Number.isFinite(money(v.current));
          return (
            <tr key={a.key}>
              <Td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{a.key}</Td>
              <Td style={{ fontSize: 12 }}>{a.bankaccountlabel || a.name || "—"}</Td>
              <Td style={{ fontSize: 12 }}>{mdy(a.to)}</Td>
              <Td><input type="date" value={v.asof} max={today} onChange={(e) => set(a.key, "asof", e.target.value)} style={inp} /></Td>
              <Td><input value={v.current} placeholder="$0.00" onChange={(e) => set(a.key, "current", e.target.value)}
                         style={{ ...inp, width: 130, textAlign: "right", borderColor: bad ? "#b3261e" : "#c8cdd2" }} /></Td>
              <Td><input value={v.available} placeholder="—" onChange={(e) => set(a.key, "available", e.target.value)}
                         style={{ ...inp, width: 130, textAlign: "right" }} /></Td>
            </tr>
          );
        })}</tbody>
      </table>
    </Card>
  );
};

const SectionCard = ({ s }) => (
  <Card style={{ marginBottom:12 }}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline",
                  gap:12, flexWrap:"wrap", marginBottom:10 }}>
      <div>
        <div style={{ fontWeight:600 }}>{s.templateName}</div>
        <div style={{ fontSize:12, color:"var(--color-text-muted)" }}>
          {s.kind}{s.sheet ? ` · sheet "${s.sheet}"` : ""} · {s.parsed} rows
          {s.rejected > 0 && ` · ${s.rejected} rejected`}
          {s.folded > 0 && ` · ${s.folded} duplicate copies dropped`}
        </div>
      </div>
    </div>

    {/* ── the checks ───────────────────────────────────────────────────────── */}
    <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:12 }}>
      <tbody>{s.checks.map((c, i) => (
        <tr key={i}>
          <Td style={{ width:64 }}><Pill status={c.status} /></Td>
          <Td style={{ fontWeight:500, whiteSpace:"nowrap" }}>{c.name}</Td>
          <Td style={{ color:"var(--color-text-secondary)" }}>{c.detail}</Td>
        </tr>
      ))}</tbody>
    </table>

    {s.rejects && s.rejects.length > 0 && <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:12, fontWeight:600, marginBottom:4 }}>Rows that could not be read</div>
      {s.rejects.map((r, i) => (
        <div key={i} style={{ fontSize:12, color:"var(--color-text-muted)" }}>
          line {r.lineNo}: {r.why}
        </div>
      ))}
    </div>}

    <div style={{ overflowX:"auto", marginBottom:12 }}>
      <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
        <thead><tr>
          <Th>Account</Th><Th>Name</Th><Th>Tied to</Th><Th>Rows</Th><Th>From</Th><Th>To</Th>
          {s.kind === "balances" ? <><Th>Low</Th><Th>High</Th><Th>Latest</Th></>
            : <><Th>Credits</Th><Th>Debits</Th><Th>Net</Th></>}
        </tr></thead>
        <tbody>{s.accounts.map((a) => (
          <tr key={a.key}>
            <Td style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{a.key}</Td>
            <Td>{a.name || "—"}</Td>
            {/* Which of the borrower's bank accounts these rows will be filed
                against, and how that was decided. A match made from a last four
                is a guess the operator should be able to see and correct. */}
            <Td style={{ fontSize:12 }}>
              {a.bankaccountlabel
                ? <>{a.bankaccountlabel}
                    <span style={{ color:"var(--color-text-muted)" }}> · {a.matchedBy}</span></>
                : a.matchedBy
                  ? <span style={{ color:"var(--color-text-muted)" }}>{a.matchedBy}</span>
                  : <span style={{ color:"#b3261e" }}>not tied to an account</span>}
            </Td>
            <TdNum>{a.rows}</TdNum>
            <Td>{a.from}</Td><Td>{a.to}</Td>
            {s.kind === "balances"
              ? <><TdNum>{fmt(a.low / 100)}</TdNum><TdNum>{fmt(a.high / 100)}</TdNum>
                  <TdNum style={{ fontWeight:500 }}>{fmt(a.latest / 100)}</TdNum></>
              : <><TdNum>{a.creditCount} · {fmt(a.creditTotal / 100)}</TdNum>
                  <TdNum>{a.debitCount} · {fmt(a.debitTotal / 100)}</TdNum>
                  <TdNum style={{ fontWeight:500 }}>{fmt((a.creditTotal + a.debitTotal) / 100)}</TdNum></>}
          </tr>
        ))}</tbody>
      </table>
    </div>

    {s.sample && s.sample.length > 0 && <SampleRows sample={s.sample} kind={s.kind} />}
  </Card>
);

// BOTH SIGNS ARE SHOWN. The operator is reading against a bank statement, where
// a deposit is positive. The stored figure follows Plaid, where a deposit is
// negative. Showing only one of them is how a sign error survives review — so
// the column that will be in the table is labelled as such, next to the one the
// statement shows.
const SampleRows = ({ sample, kind }) => {
  const [open, setOpen] = useState(false);
  return <>
    <Btn onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"} the first {sample.length} rows</Btn>
    {open && <div style={{ overflowX:"auto", marginTop:10 }}>
      <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
        <thead><tr>
          <Th>Account</Th><Th>Date</Th>
          {kind === "balances" ? <><Th>Balance</Th><Th>Available</Th></>
            : <><Th>Description</Th><Th>On the statement</Th><Th>Stored</Th><Th>Balance</Th></>}
        </tr></thead>
        <tbody>{sample.map((r, i) => (
          <tr key={i}>
            <Td style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{r.account}</Td>
            <Td>{r.date}</Td>
            {kind === "balances"
              ? <><TdNum>{fmt(r.balance)}</TdNum><TdNum>{r.available === null ? "—" : fmt(r.available)}</TdNum></>
              : <>
                  <Td style={{ maxWidth:340, overflow:"hidden", textOverflow:"ellipsis" }}>
                    {r.description}{r.pending && <span style={{ color:"#8a6100", fontSize:11 }}> · pending</span>}
                  </Td>
                  <TdNum>{fmt(r.statementAmount)}</TdNum>
                  <TdNum style={{ color:"var(--color-text-muted)" }}>{fmt(r.storedAmount)}</TdNum>
                  <TdNum>{r.balance === null ? "—" : fmt(r.balance)}</TdNum>
                </>}
          </tr>
        ))}</tbody>
      </table>
    </div>}
  </>;
};

// ── History ─────────────────────────────────────────────────────────────────
const HistoryTab = ({ api }) => {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = () => { setLoading(true);
    api.get("getBankImportRuns").then((d) => { setRows(d); setLoading(false); })
       .catch((e) => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []);
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, "uploadedutc", "desc");

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!rows.length) return <Card><div style={{ fontSize:13, color:"var(--color-text-muted)" }}>
    Nothing has been uploaded yet.</div></Card>;

  return <Card style={{ overflowX:"auto" }}>
    <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
      <thead><tr>
        <STh label="When"     sortKey="uploadedutc" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        <STh label="Borrower" sortKey="companyname" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        <STh label="File"     sortKey="filename"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        <STh label="Format"   sortKey="templatename" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        <STh label="Parsed"   sortKey="rowsparsed"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        <STh label="New"      sortKey="rowsnew"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        <STh label="Updated"  sortKey="rowsupdated" currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
        <STh label="Status"   sortKey="status"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        <Th>Why</Th>
        <STh label="By"       sortKey="uploadedby"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
      </tr></thead>
      <tbody>{sorted.map((r) => (
        <tr key={r.id}>
          <Td>{fmtDate(r.uploadedutc)}</Td>
          <Td style={{ fontWeight:500 }}>{r.companyname || "—"}</Td>
          <Td>{r.filename}</Td>
          <Td>{r.templatename || "—"}</Td>
          <TdNum>{r.rowsparsed}</TdNum>
          <TdNum>{r.rowsnew}</TdNum>
          <TdNum>{r.rowsupdated}</TdNum>
          <Td><Pill status={r.status === "posted" ? "pass"
                          : r.status === "blocked" || r.status === "failed" ? "FAIL" : "review"} /></Td>
          <Td style={{ maxWidth:360, overflow:"hidden", textOverflow:"ellipsis",
                       color:"var(--color-text-secondary)" }}>{r.blockedreason || ""}</Td>
          <Td style={{ color:"var(--color-text-muted)" }}>{r.uploadedby}</Td>
        </tr>
      ))}</tbody>
    </table>
  </Card>;
};

// ── Templates ───────────────────────────────────────────────────────────────
//
// Read-only here on purpose. A template is edited by uploading a file of that
// format and mapping its columns, which is the only way to see whether the
// mapping is right; a JSON textarea would let someone save a mapping that has
// never met a file.
const TemplatesTab = ({ api }) => {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = () => { setLoading(true);
    api.get("getBankImportTemplates", { all: 1 }).then((d) => { setRows(d); setLoading(false); })
       .catch((e) => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    {rows.map((t) => (
      <Card key={t.id} style={{ marginBottom:12, opacity: t.isactive ? 1 : 0.55 }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontWeight:600 }}>{t.name}{!t.isactive && " (inactive)"}</div>
            <div style={{ fontSize:12, color:"var(--color-text-muted)" }}>
              {t.kind} · {t.sourcetype}{t.sheetname ? ` · sheet "${t.sheetname}"` : ""}
              {t.runs > 0 && ` · used ${t.runs} time${t.runs === 1 ? "" : "s"}`}
              {t.lastused && ` · last ${fmtDate(t.lastused)}`}
            </div>
          </div>
        </div>
        {t.mappingError && <div style={{ marginTop:8, fontSize:12, color:"#b3261e" }}>
          This template's mapping will not parse: {t.mappingError}
        </div>}
        <div style={{ marginTop:8, fontSize:11, fontFamily:"var(--font-mono)",
                      color:"var(--color-text-muted)", wordBreak:"break-all" }}>
          {t.headersignature}
        </div>
      </Card>
    ))}
    {!rows.length && <Card><div style={{ fontSize:13, color:"var(--color-text-muted)" }}>
      No templates yet — they are seeded the first time a file is uploaded.</div></Card>}
  </>;
};
